import { dom } from '@omegagrid/core';
import { FormContainer } from "@omegagrid/form";
import rp, { AppComponent, Application, ParsedComponentId, MarkdownEditor, GridEditor, SyncGridEditorAdapter } from '@reporting/rp-app-host';
import { Page, File as WikiFile } from '@reporting/rp-types/wiki';
import { Sidebar, PageContainer } from './components';
import { PageEditorAdapter, PageFormAdapter, FileFormAdapter, FolderFormAdapter, MessagesGridEditorAdapter, VectorsGridEditorAdapter, WikiSyncEditorAdapter } from './adapters';
import { getSidebar } from './helpers';

const createMainSidebar = (app: Application, componentId: ParsedComponentId) => {
	const sidebar = dom.createElement<Sidebar>('rp-wiki-sidebar');
	return sidebar;
}

// wiki-page-{a}-{id}: a page of the wiki of a (0 = the application wiki), see helpers.pageComponentId
const createPageContainer = async (app: Application, componentId: ParsedComponentId) => {
	const a = parseInt(componentId.ids[1]);
	const pageId = parseInt(componentId.ids[2]);
	if (!pageId) return null;
	const component = dom.createElement<PageContainer>('rp-wiki-page-container');
	component.a = a;
	component.pageId = pageId;
	component.lang = rp.userProvider?.user?.lang;
	return component;
}

const createPageEditor = async (app: Application, componentId: ParsedComponentId) => {
	const a = parseInt(componentId.ids[1]);
	const lang = componentId.ids[2];
	const pageId = parseInt(componentId.ids[3]);
	const page = await rp.ms('wiki').get<Page>(`pages/${pageId}`, { lang, accounting: a });
	if (!page) return null;
	const component = dom.createElement<MarkdownEditor>('rp-markdown-editor');
	component.adapter = new PageEditorAdapter(a, page);
	return component;
}

const createPageForm = async (app: Application, componentId: ParsedComponentId) => {
	const a = parseInt(componentId.ids[1]);
	const lang = componentId.ids[2];
	const pageId = componentId.ids[3] ? parseInt(componentId.ids[3]) : null;
	let page: Page = { lang, id_wiki_page: pageId };
	if (pageId) {
		const existingPage = await rp.ms('wiki').get<Page>(`pages/${pageId}`, { lang, disable404: true, accounting: a });
		if (existingPage) page = existingPage; // edit
	}
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new PageFormAdapter(a, page);
	component.addEventListener('form.save', () => getSidebar()?.refresh());
	return component as unknown as AppComponent;
}

const createFileForm = async (app: Application, componentId: ParsedComponentId) => {
	const a = parseInt(componentId.ids[1]);
	const fileId = componentId.ids[2] ? parseInt(componentId.ids[2]) : null;
	let file: WikiFile = { id_wiki_file: fileId };
	if (fileId) {
		const existingFile = await rp.ms('wiki').get<WikiFile>(`files/${fileId}`, { disable404: true, accounting: a });
		if (existingFile) file = existingFile;
	}
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new FileFormAdapter(a, file);
	component.addEventListener('form.save', () => getSidebar()?.refresh());
	return component as unknown as AppComponent;
}

const createFolderForm = async (app: Application, componentId: ParsedComponentId) => {
	const a = parseInt(componentId.ids[1]);
	const lang = componentId.ids[2];
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new FolderFormAdapter(a, lang);
	component.addEventListener('form.save', () => getSidebar()?.refresh());
	return component as unknown as AppComponent;
}

const createMessagesGridEditor = (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new MessagesGridEditorAdapter(0);
	return component;
}

// wiki-vectors-{a}: the semantic-search index of the wiki (a = 0 for the application documentation, otherwise the customer's own)
const createVectorsGridEditor = (app: Application, componentId: ParsedComponentId) => {
	const a = parseInt(componentId.ids[1]);
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new VectorsGridEditorAdapter(isNaN(a) ? 0 : a);
	return component;
}

// wiki-sync: the synchronization and sharing of the company wiki with other companies, as the grid the admin module shows for every module
const createSyncEditor = (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new SyncGridEditorAdapter(new WikiSyncEditorAdapter());
	return component;
}

export const componentFactories = new Map<string, (app: Application, componentId: ParsedComponentId) => AppComponent|Promise<AppComponent>>([
	['wiki', createMainSidebar],
	['page', createPageContainer],
	['pageEditor', createPageEditor],
	['pageForm', createPageForm],
	['fileForm', createFileForm],
	['folderForm', createFolderForm],
	['messages', createMessagesGridEditor],
	['vectors', createVectorsGridEditor],
	['sync', createSyncEditor],
]);
